import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import heroesData from '../assets/data/superheroes.json'

const SITE_URL = 'https://www.communityofguardians.com'
const SITE_NAME = 'Community of Guardians'
const DEFAULT_DESCRIPTION =
  'Website of the Community of Guardians, a global movement of everyday heroes united to take action for a better world.'

type Meta = { title: string; description: string; noindex?: boolean }

const STATIC_META: Record<string, Meta> = {
  '/': {
    title: `${SITE_NAME} | Translating Scientific Knowledge into Collective Action`,
    description:
      'A global framework uniting researchers, educators, governments and the next generation to solve pressing challenges through the UN Sustainable Development Goals.',
  },
  '/about': {
    title: `About Us | ${SITE_NAME}`,
    description:
      'Learn how the Community of Guardians connects students, researchers and changemakers to turn ideas into real-world action on the SDGs.',
  },
  '/explore': {
    title: `Explore the Movement | ${SITE_NAME}`,
    description:
      'Why collective action matters, how the Community of Guardians works, our vision for 2030, and where to follow us.',
  },
  '/superheroes': {
    title: `SDG Superheroes | ${SITE_NAME}`,
    description:
      'Meet the 17 SDG Superheroes, each representing a UN Sustainable Development Goal, and find the one that matches you.',
  },
  '/research': {
    title: `Research Inventory | ${SITE_NAME}`,
    description:
      'Explore research aligned with the UN Sustainable Development Goals, curated by the Community of Guardians.',
  },
  '/contact': {
    title: `Contact Us | ${SITE_NAME}`,
    description:
      'Get in touch with the Community of Guardians team for partnerships, questions and collaboration.',
  },
  '/quiz': {
    title: `Find Your SDG Superhero | ${SITE_NAME}`,
    description:
      'Take the 2-minute SDG Quest and discover which Sustainable Development Goal superhero you are.',
  },
  '/coming-soon': {
    title: `Coming Soon | ${SITE_NAME}`,
    description: DEFAULT_DESCRIPTION,
    noindex: true,
  },
}

const getMeta = (pathname: string): Meta => {
  const path = pathname.length > 1 ? pathname.replace(/\/$/, '') : pathname
  if (STATIC_META[path]) return STATIC_META[path]

  const heroMatch = path.match(/^\/(superhero|quiz-result)\/(\d+)$/)
  if (heroMatch) {
    const hero = heroesData.find((h) => h.id.toString() === heroMatch[2])
    if (hero) {
      const isResult = heroMatch[1] === 'quiz-result'
      return {
        title: `${hero.name} | ${SITE_NAME}`,
        description: hero.description,
        noindex: isResult,
      }
    }
  }

  // Quiz steps, unknown routes, etc. should not be indexed.
  return {
    title: SITE_NAME,
    description: DEFAULT_DESCRIPTION,
    noindex: path !== '/',
  }
}

const setMeta = (selector: string, attr: string, value: string) => {
  const el = document.head.querySelector(selector)
  if (el) el.setAttribute(attr, value)
}

/** Updates title, description, canonical, social tags and robots on route change. */
export const usePageMeta = () => {
  const { pathname } = useLocation()

  useEffect(() => {
    const { title, description, noindex } = getMeta(pathname)
    const url =
      SITE_URL + (pathname === '/' ? '/' : pathname.replace(/\/$/, ''))

    document.title = title
    setMeta('meta[name="description"]', 'content', description)
    setMeta('meta[property="og:title"]', 'content', title)
    setMeta('meta[property="og:description"]', 'content', description)
    setMeta('meta[property="og:url"]', 'content', url)
    setMeta('meta[property="twitter:title"]', 'content', title)
    setMeta('meta[property="twitter:description"]', 'content', description)
    setMeta('meta[property="twitter:url"]', 'content', url)
    setMeta('link[rel="canonical"]', 'href', url)
    setMeta(
      'meta[name="robots"]',
      'content',
      noindex ? 'noindex' : 'index, follow',
    )
  }, [pathname])
}
